/**
 * Iter 253.1 — AuthContext refondu pour Phase 2 token security.
 *
 * Changements vs Phase 1 :
 *  - Plus de `localStorage.getItem("blitzz_token")` au mount (token réside dans le cookie HttpOnly côté serveur)
 *  - Plus de `localStorage.setItem("blitzz_token", t)` au login (le backend pose le cookie automatiquement)
 *  - On stocke uniquement le `csrfToken` en state (lu depuis le cookie non-HttpOnly `csrf_token`)
 *  - Sur toutes les mutations (POST/PUT/PATCH/DELETE), header `X-CSRF-Token` injecté automatiquement
 *  - Au mount : on tente `/auth/me` (qui lit le cookie), si 200 → on récupère le user
 *  - Pour l'impersonation, on n'a plus besoin de stocker le token admin côté JS : le backend
 *    pourra (futur) maintenir une pile via cookie HttpOnly dédié. Pour l'instant on retire
 *    simplement la mémoire JS du admin_token (le user_id admin est connu via JWT claim
 *    `impersonated_by` qu'on peut renvoyer au backend pour stop-impersonation).
 */
import { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";
import { makeOfflineApi, bindRawApi } from "../lib/offline/offlineApi";
import { salarieOptimistic, SALARIE_NO_QUEUE } from "../lib/offline/salarieOffline";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Helper: read non-HttpOnly cookie value by name
function applyProfileLanguage(u) {
  // La langue du profil prime (choisie une fois, appliquée sur le web ET l'app)
  const lang = u?.language;
  if (!lang || !["fr", "en", "es", "pt"].includes(lang)) return;
  let stored = null;
  try { stored = localStorage.getItem("blitzz_lang"); } catch (_) {}
  if (stored !== lang) {
    try { localStorage.setItem("blitzz_lang", lang); } catch (_) {}
    if (!sessionStorage.getItem("blitzz_lang_reloaded")) { sessionStorage.setItem("blitzz_lang_reloaded", "1"); window.location.reload(); }
  }
}


function readCookie(name) {
  if (typeof document === "undefined") return null;
  const match = document.cookie
    .split("; ")
    .find((row) => row.startsWith(name + "="));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
}

// ─── Iter 256 — Rotating refresh tokens ─────────────────────────────────────
// Single-flight: concurrent 401s share one /auth/refresh call (rotation is
// single-use server-side, parallel refreshes would revoke the family).
let _refreshPromise = null;
function refreshSession() {
  if (!_refreshPromise) {
    _refreshPromise = axios
      .post(`${API}/auth/refresh`, {}, { withCredentials: true })
      .finally(() => {
        _refreshPromise = null;
      });
  }
  return _refreshPromise;
}

// ─── Iter 318 — Instance axios UNIQUE et stable ──────────────────────────────
// Les en-têtes CSRF / X-Societe-Id sont lus à chaque requête (cookie + localStorage),
// plus jamais figés à la création : un rafraîchissement de session dans un autre
// onglet ne produit plus de "CSRF token invalide". L'instance étant stable, les
// composants qui dépendent de `api` dans un useEffect ne rebouclent plus.
// Iter 321 — profil utilisateur mis en cache pour le démarrage hors ligne (PWA / Electron)
const USER_CACHE_KEY = "blitzz_user_cache";
function cacheUser(u) {
  try { if (u) localStorage.setItem(USER_CACHE_KEY, JSON.stringify(u)); else localStorage.removeItem(USER_CACHE_KEY); } catch { /* quota */ }
}
function readCachedUser() {
  try { const raw = localStorage.getItem(USER_CACHE_KEY); return raw ? JSON.parse(raw) : null; } catch { return null; }
}

let _onSessionLost = null;
const sharedApi = axios.create({ baseURL: API, withCredentials: true });

sharedApi.interceptors.request.use((config) => {
  config.headers = config.headers || {};
  const csrf = readCookie("csrf_token");
  if (csrf) config.headers["X-CSRF-Token"] = csrf;
  const sid = typeof window !== "undefined" ? localStorage.getItem("blitzz_societe_id") : null;
  if (sid && sid !== "__group__") config.headers["X-Societe-Id"] = sid;
  else delete config.headers["X-Societe-Id"];
  return config;
});

sharedApi.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;
    const status = error.response?.status;
    const url = original?.url || "";
    const isAuthEntry = url.includes("/auth/login") || url.includes("/auth/refresh") || url.includes("/auth/logout");
    if (status === 401 && original && !original._retry && !isAuthEntry) {
      original._retry = true;
      try {
        await refreshSession();
        return sharedApi(original);
      } catch (e2) {
        if (e2?.response) _onSessionLost?.(); // hors ligne : on garde la session locale
      }
    }
    return Promise.reject(error);
  },
);

// ─── Iter 335 — Mode "Blitzz Salarié" : API hors ligne transparente pour toutes les pages sous /salarie ──
let _salarieMode = false;
let _salarieApi = null;
export const setSalarieOfflineMode = (on) => { _salarieMode = !!on; };
export const isSalarieOfflineMode = () => _salarieMode;
function getSalarieApi() {
  if (!_salarieApi) {
    bindRawApi(sharedApi);
    _salarieApi = makeOfflineApi(sharedApi, { scope: "salarie", onQueued: salarieOptimistic, noQueue: SALARIE_NO_QUEUE });
  }
  return _salarieApi;
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  // Iter 253.1 — In-memory CSRF token, sync'd from non-HttpOnly cookie on demand
  const [csrfToken, setCsrfToken] = useState(() => readCookie("csrf_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    _onSessionLost = () => setUser(null);
    return () => { _onSessionLost = null; };
  }, []);

  // Iter 318 — retourne toujours la même instance (en-têtes injectés à la volée)
  // Iter 335 — dans l'app Blitzz Salarié, l'instance est enveloppée (cache IndexedDB + file d'attente hors ligne)
  const axiosAuth = useCallback(() => (_salarieMode ? getSalarieApi() : sharedApi), []);

  const checkAuth = useCallback(async () => {
    // OAuth callback path — let AuthCallback handle session exchange first
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Pages publiques : pas de session à vérifier (évite les 401 en console).
    // Sur /login on ne tente /auth/me que si un indice de session existe (cookie csrf lisible).
    const path = window.location.pathname;
    if ((path === "/login" || path === "/register" || path.startsWith("/resultat/")) && !readCookie("csrf_token")) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
      setUser(res.data);
      applyProfileLanguage(res.data);
      cacheUser(res.data);
      // Refresh CSRF token from cookie (server may have rotated it)
      setCsrfToken(readCookie("csrf_token"));
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) {
        // Iter 256 — access token expired: try the refresh token before giving up
        try {
          const r = await refreshSession();
          setCsrfToken(r.data?.csrf_token || readCookie("csrf_token"));
          const retry = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(retry.data);
          cacheUser(retry.data);
        } catch (e2) {
          // Iter 321 — hors ligne : on conserve la session locale (app installée / tablette)
          if (!e2?.response && readCachedUser()) setUser(readCachedUser());
          else { setUser(null); cacheUser(null); }
        }
      } else if (status === 403) {
        setUser(null);
        cacheUser(null);
      } else if (!status || status === 429) {
        // Iter 321 — réseau indisponible (ou rate-limit) : session restaurée depuis le cache local
        const cached = readCachedUser();
        if (cached) setUser(cached);
      }
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const applyLoginResponse = (data) => {
    const { user: u, csrf_token } = data;
    setCsrfToken(csrf_token || readCookie("csrf_token"));
    setUser(u);
    cacheUser(u);
    applyProfileLanguage(u);
    return u;
  };

  const login = async (email, password) => {
    const res = await axios.post(
      `${API}/auth/login`,
      { email, password },
      { withCredentials: true },
    );
    // Double authentification : le serveur renvoie un défi au lieu des jetons
    if (res.data?.requires_2fa) return { requires_2fa: true, ...res.data };
    return applyLoginResponse(res.data);
  };

  const verify2fa = async (challenge_id, code, remember_device) => {
    const res = await axios.post(`${API}/auth/2fa/verify`, { challenge_id, code, remember_device }, { withCredentials: true });
    return applyLoginResponse(res.data);
  };

  const register = async (email, password, name) => {
    const res = await axios.post(
      `${API}/auth/register`,
      { email, password, name },
      { withCredentials: true },
    );
    const { user: u, csrf_token } = res.data;
    setCsrfToken(csrf_token || readCookie("csrf_token"));
    setUser(u);
    return u;
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processSession = async (sessionId) => {
    const res = await axios.post(
      `${API}/auth/session?session_id=${sessionId}`,
      {},
      { withCredentials: true },
    );
    const { user: u, csrf_token } = res.data;
    setCsrfToken(csrf_token || readCookie("csrf_token"));
    setUser(u);
    return u;
  };

  const logout = async () => {
    try {
      const headers = {};
      const csrf = readCookie("csrf_token") || csrfToken;
      if (csrf) headers["X-CSRF-Token"] = csrf;
      await axios.post(`${API}/auth/logout`, {}, { headers, withCredentials: true });
    } catch { /* ignore */ }
    setUser(null);
    setCsrfToken(null);
    cacheUser(null);
  };

  const impersonate = async (userId, reason = "") => {
    const headers = {};
    const csrf = readCookie("csrf_token") || csrfToken;
    if (csrf) headers["X-CSRF-Token"] = csrf;
    const res = await axios.post(
      `${API}/master/impersonate/${userId}`,
      { reason },
      { headers, withCredentials: true },
    );
    const { user: newUser, csrf_token: newCsrf } = res.data;
    setCsrfToken(newCsrf || readCookie("csrf_token"));
    setUser(newUser);
    window.location.href = "/dashboard";
    return newUser;
  };

  const stopImpersonation = async () => {
    const headers = {};
    const csrf = readCookie("csrf_token") || csrfToken;
    if (csrf) headers["X-CSRF-Token"] = csrf;
    try {
      const res = await axios.post(`${API}/master/stop-impersonation`, {}, { headers, withCredentials: true });
      const { csrf_token: newCsrf } = res.data || {};
      setCsrfToken(newCsrf || readCookie("csrf_token"));
    } catch (e) { /* ignore */ }
    window.location.href = "/admin/master";
  };

  // Iter 253.1 — Impersonation status derives from the JWT claim only (no more localStorage)
  const isImpersonating = !!(user?.impersonated_by);

  const hasPermission = (permKey) => {
    if (!user) return false;
    const role = user.role;
    if (role === "superadmin" || role === "admin" || role === "tenant_admin") return true;
    const perms = user.module_permissions || {};
    return !!perms[permKey];
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        // Iter 253.1 — `token` is no longer exposed (lives in HttpOnly cookie).
        // Components that imported `token` should switch to using `axiosAuth()` exclusively.
        token: null,
        csrfToken,
        loading,
        login, verify2fa,
        register,
        loginWithGoogle,
        processSession,
        logout,
        checkAuth,
        impersonate,
        stopImpersonation,
        isImpersonating,
        axiosAuth,
        hasPermission,
        isAdmin: user?.role === "admin" || user?.role === "superadmin",
        isSuperadmin: user?.role === "superadmin",
        isStaff: ["superadmin", "admin", "coach", "sous_traitant", "comptable", "tenant_admin", "tenant_member"].includes(user?.role),
        isCoach: user?.role === "coach",
        isSousTraitant: user?.role === "sous_traitant",
        isTenant: user?.role === "tenant_admin" || user?.role === "tenant_member",
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
